import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';

import { SportTrainerModel } from '../../../sports/models/sport-trainer.model';
import { SportService } from '../../../sports/services/sports.service';
import { AssignmentExchangeModel } from '../../models/assignment-exchange.model';
import { AssignmentSubstituteModel } from '../../models/assignments-substitute.model';
import { AssignmentsService } from '../../services/assignments.service';

@Component({
    selector: 'app-exchange-assignments-modal',
    templateUrl: './exchange-assignments-modal.component.html',
    styleUrl: './exchange-assignments-modal.component.scss'
})
export class ExchangeAssignmentsModalComponent implements OnInit {
    @Output() onSuccess = new EventEmitter<boolean>();
    @Output() onFullSuccess = new EventEmitter<boolean>();
    @Input() exchangeAssignments: AssignmentExchangeModel[] = [];
    @Input() isVacacyAllowed: boolean = false;

    isMoreThenOneSportSelected: boolean = false;

    isCloseOnly: boolean = false;
    trainers$!: Observable<SportTrainerModel[] | null>;
    exchangeAssignmentsForm!: FormGroup;
    selectedTrainerName: string = '';
    isLoading: boolean = false;

    private trainerSubject = new BehaviorSubject<SportTrainerModel[] | null>(null);
    protected trainerWithMissingSport: SportTrainerModel[] = [];
    protected trainerWithoutMissingSport: SportTrainerModel[] = [];

    constructor(
        private sportService: SportService,
        private formBuilder: FormBuilder,
        private assignmentService: AssignmentsService,
    ) { }

    ngOnInit(): void {
        this.initForm();
        this.loadInitialDate();
        this.onTrainerSelectionChange();
        this.checkIfMoreThenOneSportSelected();
    }

    public sendSubsituteAssignments(substituteAssignments: AssignmentSubstituteModel): void {
        this.isLoading = true;
        this.assignmentService.sendSubstituteAssignments(substituteAssignments).subscribe(
            {
                next: (response) => {
                    response.forEach((resultModel) => {
                        const matchingAssignment = this.exchangeAssignments.
                            find((assignment) => assignment.assignmentId == resultModel.assignmentId);

                        if (matchingAssignment) {
                            matchingAssignment.isSuccess = resultModel.isSuccessful;
                            matchingAssignment.errorMessage = resultModel.errorMessage;
                        }
                    });
                    this.onSuccess.emit(true);
                    // emit if all exchanges are successful
                    if (response.every(x => x.isSuccessful)) this.onFullSuccess.emit(true);
                    this.isLoading = false;
                },
                error: (error) => {
                    this.isLoading = false;
                    this.exchangeAssignmentsForm.controls['selectedTrainer']?.enable();
                }
            }
        )
        this.isCloseOnly = true;
    }

    protected setSubstitute(): void {
        this.exchangeAssignmentsForm.markAllAsTouched();
        if (!this.exchangeAssignmentsForm?.valid) return;

        this.exchangeAssignmentsForm.controls['selectedTrainer']?.disable();

        let subsituteAssignments = this.createAssignmentsSubstituteModel();

        this.sendSubsituteAssignments(subsituteAssignments);
    }

    protected isAssignmentOnSameDay(from: Date | undefined, to: Date | undefined): boolean {
        if (!from || !to) return false;
        return new Date(from).toDateString() === new Date(to).toDateString();
    }

    protected getCurrentSelectedTrainer(): number {
        return this.exchangeAssignmentsForm.get('selectedTrainer')?.value;
    }

    // Method to get trainers with isMissingSport flag set to true
    protected getTrainersWithMissingSport(): SportTrainerModel[] {
        return this.trainerWithMissingSport = this.trainerSubject.getValue()?.filter((trainer) => trainer.isSportMissing) || [];
    }

    // Method to get trainers with isMissingSport flag set to false
    protected getTrainersWithoutMissingSport(): SportTrainerModel[] {
        return this.trainerWithoutMissingSport = this.trainerSubject.getValue()?.filter((trainer) => !trainer.isSportMissing) || [];
    }

    private initForm(): void {
        this.exchangeAssignmentsForm = this.formBuilder.group({
            selectedTrainer: [null, Validators.required]
        })
        this.exchangeAssignmentsForm.markAllAsTouched();
    }

    private loadInitialDate(): void {
        if (this.exchangeAssignments.length > 0 && this.exchangeAssignments[0].sportId) {

            this.exchangeAssignments.sort((a, b) => {
                const dateA = a.from ? new Date(a.from).getTime() : 0;
                const dateB = b.to ? new Date(b.to).getTime() : 0;
                return dateA - dateB;
            });

            var sportIds: number[] = this.exchangeAssignments.flat().map(x => x.sportId!);
            this.loadTrainers(sportIds);
        }
        this.trainers$ = this.trainerSubject;
    }

    private loadTrainers(sportIds: number[]) {
        this.sportService.getTrainers(sportIds).subscribe({
            next: trainers => {
                trainers = trainers.sort((a, b) => (a.firstName + b.lastName).localeCompare((b.firstName + b.lastName)));
                this.trainerSubject.next(trainers);
                this.updateTrainerLists(trainers);
            }
        });
    }

    private updateTrainerLists(trainers: SportTrainerModel[]): void {
        this.trainerWithMissingSport = trainers.filter((trainer) => trainer.isSportMissing);
        this.trainerWithoutMissingSport = trainers.filter((trainer) => !trainer.isSportMissing);
    }

    private createAssignmentsSubstituteModel(): AssignmentSubstituteModel {
        var selectedTrainer: number | null = this.exchangeAssignmentsForm.value.selectedTrainer;
        if (selectedTrainer == 0) selectedTrainer = null;

        return {
            userId: selectedTrainer,
            assignmentIds: this.exchangeAssignments
                .filter((id) => id !== undefined)
                .map(assignment => assignment.assignmentId)

        };
    }

    private onTrainerSelectionChange(): void {
        this.exchangeAssignmentsForm.get('selectedTrainer')?.valueChanges.subscribe((trainerId: number) => {
            const selectedTrainer = this.trainerSubject.getValue()?.find(trainer => trainer.id === trainerId);

            this.selectedTrainerName = selectedTrainer ? `${selectedTrainer.firstName} ${selectedTrainer.lastName}` : '';
        });
    }

    private checkIfMoreThenOneSportSelected(): void {
        if (this.exchangeAssignments == null) return;
        if (this.exchangeAssignments.length <= 1) this.isMoreThenOneSportSelected = false;

        let sports = this.exchangeAssignments.flat().filter(x => x.sportId != undefined).map(x => x.sportId!);
        sports = sports.filter((value, index, array) => array.indexOf(value) === index);
        this.isMoreThenOneSportSelected = (sports.length > 1);
    }
}
